import { Action, ActionReducer } from '@ngrx/store'

const INIT_ACTION = '@ngrx/store/init'
const UPDATE_ACTION = '@ngrx/store/update-reducers'

const isoDate = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/

const dateReviver = (_key: string, value: unknown): unknown =>
  typeof value === 'string' && isoDate.test(value) ? new Date(value) : value

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

const clone = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(clone)
  }
  if (value instanceof Date) {
    return new Date(value.getTime())
  }
  if (isPlainObject(value)) {
    return Object.keys(value).reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = clone(value[key])
      return acc
    }, {})
  }
  return value
}

const deepMerge = (target: unknown, source: unknown): unknown => {
  if (!isPlainObject(target) || !isPlainObject(source)) {
    return clone(source)
  }
  return Object.keys(source).reduce<Record<string, unknown>>(
    (acc, key) => {
      acc[key] = deepMerge(target[key], source[key])
      return acc
    },
    { ...target }
  )
}

const looksLikeJson = (raw: string): boolean =>
  raw === 'null' ||
  raw === 'true' ||
  raw === 'false' ||
  raw.charAt(0) === '{' ||
  raw.charAt(0) === '['

const rehydrate = (
  keys: string[],
  storage: Storage
): Record<string, unknown> =>
  keys.reduce<Record<string, unknown>>((acc, key) => {
    const raw = storage.getItem(key)
    if (raw === null) {
      return acc
    }
    try {
      acc[key] = looksLikeJson(raw) ? JSON.parse(raw, dateReviver) : raw
    } catch {
      return acc
    }
    return acc
  }, {})

const sync = (
  state: Record<string, unknown>,
  keys: string[],
  storage: Storage,
  removeOnUndefined: boolean
): void => {
  keys.forEach((key) => {
    const slice = state[key]
    if (slice !== undefined) {
      try {
        storage.setItem(
          key,
          typeof slice === 'string' ? slice : JSON.stringify(slice)
        )
      } catch {
        return
      }
    } else if (removeOnUndefined) {
      try {
        storage.removeItem(key)
      } catch {
        return
      }
    }
  })
}

export interface StorageSyncConfig {
  keys: string[]
  storage: Storage
  rehydrate?: boolean
  removeOnUndefined?: boolean
}

export function localStorageSync(
  config: StorageSyncConfig
): (reducer: ActionReducer<any>) => ActionReducer<any> {
  const { keys, storage, removeOnUndefined = false } = config

  return (reducer: ActionReducer<any>): ActionReducer<any> => {
    const rehydratedState = config.rehydrate ? rehydrate(keys, storage) : undefined

    return (state: any, action: Action) => {
      let nextState =
        action.type === INIT_ACTION && !state
          ? reducer(state, action)
          : { ...state }

      if (
        (action.type === INIT_ACTION || action.type === UPDATE_ACTION) &&
        rehydratedState
      ) {
        nextState = deepMerge(nextState, rehydratedState)
      }

      nextState = reducer(nextState, action)

      if (action.type !== INIT_ACTION) {
        sync(nextState, keys, storage, removeOnUndefined)
      }

      return nextState
    }
  }
}
